import { useScrollReveal } from "@/hooks/useScrollReveal";
import type { Service } from "@/mocks/services";

interface ServicesGridProps {
  title: string;
  subtitle: string;
  services: Service[];
  eyebrow?: string;
}

// ─── Featured Card (index 0) ─────────────────────────────────────────────────
function FeaturedCard({
  service,
  isVisible,
}: {
  service: Service;
  isVisible: boolean;
}) {
  return (
    <a
      href={service.href}
      rel="noopener noreferrer"
      className={`group block bg-white rounded-2xl overflow-hidden border border-cream-dark/60 transition-all duration-600 hover:-translate-y-2 cursor-pointer col-span-1 md:col-span-2 lg:col-span-2 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: "0ms" }}
    >
      {/* Large Image */}
      <div className="w-full h-64 lg:h-80 overflow-hidden relative bg-charcoal-light">
        <img
          src={service.image}
          alt={service.title}
          loading="eager"
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/55 via-transparent to-transparent" />
        {service.tag && (
          <div className="absolute top-4 left-4">
            <span className="inline-block bg-gold-DEFAULT text-charcoal text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap tracking-wide">
              {service.tag}
            </span>
          </div>
        )}
        {/* Bottom-left overlay content */}
        <div className="absolute bottom-5 left-6 right-6 hidden lg:block">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gold-DEFAULT/20 border border-gold-DEFAULT/40 flex-shrink-0">
              <i className={`${service.icon} text-gold-DEFAULT text-base`} />
            </div>
            <h3 className="font-serif text-white text-2xl leading-tight">
              {service.title}
            </h3>
          </div>
        </div>
      </div>

      {/* Content — visible on all, overlaid on lg */}
      <div className="p-7 lg:p-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-2.5 lg:hidden">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gold-DEFAULT/12 flex-shrink-0">
            <i className={`${service.icon} text-gold-DEFAULT text-base`} />
          </div>
          <h3 className="font-serif text-charcoal text-xl leading-tight">
            {service.title}
          </h3>
        </div>
        <p className="text-sm text-charcoal-soft leading-relaxed font-sans lg:max-w-md">
          {service.description}
        </p>
        <span className="inline-flex items-center gap-2 bg-charcoal text-white text-xs font-bold px-6 py-3 rounded-full hover:bg-charcoal-light transition-colors cursor-pointer whitespace-nowrap tracking-wide flex-shrink-0">
          Explore
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-arrow-right-line" />
          </div>
        </span>
      </div>
    </a>
  );
}

// ─── Standard Card ────────────────────────────────────────────────────────────
function StandardCard({
  service,
  index,
  isVisible,
  tall,
}: {
  service: Service;
  index: number;
  isVisible: boolean;
  tall?: boolean;
}) {
  return (
    <a
      href={service.href}
      rel="noopener noreferrer"
      className={`group block bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1.5 cursor-pointer border border-cream-dark/60 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 75}ms` }}
    >
      <div className={`w-full ${tall ? "h-64 lg:h-80" : "h-44"} overflow-hidden relative bg-charcoal-light`}>
        <img
          src={service.image}
          alt={service.title}
          loading="eager"
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-600"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        {service.tag && (
          <div className="absolute top-3 left-3">
            <span className="inline-block bg-gold-DEFAULT text-charcoal text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap tracking-wide">
              {service.tag}
            </span>
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gold-DEFAULT/12 flex-shrink-0">
            <i className={`${service.icon} text-gold-DEFAULT text-base`} />
          </div>
          <h3 className="font-serif text-charcoal text-lg leading-tight">
            {service.title}
          </h3>
        </div>
        <p className="text-sm text-charcoal-soft leading-relaxed font-sans mb-4">
          {service.description}
        </p>
        <span className="inline-flex items-center gap-1.5 text-gold-DEFAULT text-xs font-bold hover:text-gold-dark transition-colors cursor-pointer whitespace-nowrap tracking-wide uppercase">
          Learn More
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-200" />
          </div>
        </span>
      </div>
    </a>
  );
}

// ─── Wide Horizontal Card (last card) ────────────────────────────────────────
function WideCard({
  service,
  index,
  isVisible,
}: {
  service: Service;
  index: number;
  isVisible: boolean;
}) {
  return (
    <a
      href={service.href}
      rel="noopener noreferrer"
      className={`group flex flex-col lg:flex-row bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 cursor-pointer border border-cream-dark/60 col-span-1 md:col-span-2 lg:col-span-3 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 75}ms` }}
    >
      {/* Image */}
      <div className="w-full lg:w-5/12 h-52 lg:h-60 overflow-hidden relative flex-shrink-0 bg-charcoal-light">
        <img
          src={service.image}
          alt={service.title}
          loading="eager"
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-charcoal/15" />
        {service.tag && (
          <div className="absolute top-4 left-4">
            <span className="inline-block bg-gold-DEFAULT text-charcoal text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap tracking-wide">
              {service.tag}
            </span>
          </div>
        )}
      </div>
      {/* Content */}
      <div className="flex-1 p-8 lg:p-10 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-gold-DEFAULT/12 flex-shrink-0">
            <i className={`${service.icon} text-gold-DEFAULT text-xl`} />
          </div>
          <h3 className="font-serif text-charcoal text-2xl leading-tight">
            {service.title}
          </h3>
        </div>
        <p className="text-sm text-charcoal-soft leading-relaxed font-sans mb-6 max-w-2xl">
          {service.description}
        </p>
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-2 bg-gold-DEFAULT text-charcoal text-xs font-bold px-7 py-3 rounded-full hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap tracking-wide">
            Get Started
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </span>
          <a
            href="tel:9527693208"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-2 text-charcoal-soft text-xs font-semibold hover:text-gold-DEFAULT transition-colors cursor-pointer whitespace-nowrap"
            aria-label="Call (952) 769-3208"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-gold-DEFAULT" />
            </div>
            (952) 769-3208
          </a>
        </div>
      </div>
    </a>
  );
}

// ─── Main Export ─────────────────────────────────────────────────────────────
export default function ServicesGrid({
  title,
  subtitle,
  services,
  eyebrow = "WHAT WE OFFER",
}: ServicesGridProps) {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal();

  const hasWide = services.length >= 6;
  const mainServices = hasWide ? services.slice(0, services.length - 1) : services;
  const wideService = hasWide ? services[services.length - 1] : null;

  return (
    <section className="bg-white py-20 md:py-28" aria-labelledby="services-heading">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-6 transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-gold-DEFAULT" />
              <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
                {eyebrow}
              </span>
            </div>
            <h2
              id="services-heading"
              className="font-serif text-charcoal leading-tight max-w-lg"
              style={{ fontSize: "clamp(1.9rem, 3.5vw, 2.9rem)", lineHeight: 1.1 }}
            >
              {title}
            </h2>
          </div>
          <p className="text-charcoal-soft font-sans text-base leading-relaxed max-w-md lg:text-right">
            {subtitle}
          </p>
        </div>

        {/* Asymmetric Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mainServices.map((service, i) => {
            if (i === 0) {
              return (
                <FeaturedCard key={service.id} service={service} isVisible={gridVisible} />
              );
            }
            // Index 1: tall card to match featured
            const isTall = i === 1;
            return (
              <StandardCard
                key={service.id}
                service={service}
                index={i}
                isVisible={gridVisible}
                tall={isTall}
              />
            );
          })}
          {/* Wide horizontal card — last item */}
          {wideService && (
            <WideCard
              service={wideService}
              index={mainServices.length}
              isVisible={gridVisible}
            />
          )}
        </div>

        {/* Post-services CTA */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://www.uagmn.com/inventory/"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-charcoal text-white font-semibold text-sm px-9 py-4 rounded-full hover:bg-charcoal-light transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Browse Full Inventory
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </div>
          </a>
          <a
            href="https://www.uagmn.com/apply-online/"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border-2 border-charcoal/25 text-charcoal font-semibold text-sm px-9 py-4 rounded-full hover:bg-charcoal hover:text-white hover:border-charcoal transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Apply for Financing
          </a>
        </div>
      </div>
    </section>
  );
}
